"use client";

import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { SerialDilutionCalculatorStrings } from "@/components/tools/serial-dilution-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  fixed,
  type NumberFormatter,
  plain,
  sig,
  toTsv,
} from "@/lib/tool-kit/biology-lab/format";
import {
  buildSeries,
  type CfuInput,
  COUNT_RANGES,
  type CountRangeId,
  cfuFromCounts,
  judgePlate,
  type SeriesError,
  type SeriesInput,
  tenFoldNotation,
} from "@/lib/tool-kit/biology-lab/serial-dilution";

type Strings = SerialDilutionCalculatorStrings;

const WORKING_BOX =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";

function seriesError(code: SeriesError, strings: Strings): string {
  const messages: Record<SeriesError, string> = {
    factor: strings.errFactor,
    steps: strings.errSteps,
    volume: strings.errVolume,
    start: strings.errStart,
  };
  return messages[code];
}

/** The dilution table: what to move, how much diluent, what each tube holds. */
export function SeriesPanel({
  input,
  volumeUnit,
  concUnit,
  fmt,
  strings,
}: {
  input: SeriesInput;
  volumeUnit: string;
  concUnit: string;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const outcome = buildSeries(input);
  if ("error" in outcome) {
    return <ErrorNotice>{seriesError(outcome.error, strings)}</ErrorNotice>;
  }
  const { plan } = outcome;
  const first = plan.rows[0];
  const head = [
    strings.colTube,
    strings.colOverall,
    strings.colConcentration,
    strings.colTransfer,
    strings.colDiluent,
    strings.colLeft,
  ];
  const rows = plan.rows.map((row) => {
    const notation = tenFoldNotation(input.factor, row.tube);
    return [
      plain(fmt, row.tube),
      notation
        ? `1:${plain(fmt, row.cumulative)} (${notation})`
        : `1:${plain(fmt, row.cumulative)}`,
      row.concentration === null
        ? strings.none
        : `${sig(fmt, row.concentration, 4)} ${concUnit}`.trim(),
      row.tube === 1
        ? `${sig(fmt, row.transfer, 4)} ${strings.fromStock}`
        : `${sig(fmt, row.transfer, 4)} ${strings.fromPrevious}`,
      sig(fmt, row.diluent, 4),
      sig(fmt, row.remaining, 4),
    ];
  });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={`${strings.transferEach} (${volumeUnit})`}
          value={sig(fmt, first.transfer, 4)}
        />
        <StatCard
          label={`${strings.diluentEach} (${volumeUnit})`}
          value={sig(fmt, first.diluent, 4)}
        />
        <StatCard
          label={`${strings.diluentTotal} (${volumeUnit})`}
          value={sig(fmt, plan.totalDiluent, 4)}
        />
      </StatGrid>
      <DataTable head={head} rows={rows} />
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.workingTransfer, {
            volume: plain(fmt, input.volume),
            factor: plain(fmt, input.factor),
            transfer: sig(fmt, first.transfer, 4),
          })}
        </p>
        <p>
          {t(strings.workingDiluent, {
            volume: plain(fmt, input.volume),
            transfer: sig(fmt, first.transfer, 4),
            diluent: sig(fmt, first.diluent, 4),
          })}
        </p>
      </div>
      <p className="text-muted-foreground text-xs">
        {t(strings.seriesNote, { unit: volumeUnit })}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyTable}
        small
        text={toTsv(head, rows)}
      />
    </>
  );
}

function cfuErrorText(code: string, strings: Strings): string {
  const messages: Record<string, string> = {
    counts: strings.errCounts,
    cumulative: strings.errCumulative,
    plated: strings.errPlated,
    predilution: strings.errPre,
  };
  return messages[code] ?? strings.errCounts;
}

function plateLine(
  index: number,
  count: number,
  range: CountRangeId,
  fmt: NumberFormatter,
  strings: Strings
): string {
  const verdict = judgePlate(count, range);
  const [low, high] = COUNT_RANGES[range];
  const templates = {
    ok: strings.plateOk,
    low: strings.plateLow,
    high: strings.plateHigh,
  };
  return t(templates[verdict], {
    n: plain(fmt, index + 1),
    count: plain(fmt, count),
    low: plain(fmt, low),
    high: plain(fmt, high),
  });
}

/** Colony counts back to CFU/mL of the original sample. */
export function CfuPanel({
  input,
  range,
  fmt,
  strings,
}: {
  input: CfuInput;
  range: CountRangeId;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const outcome = cfuFromCounts(input);
  if ("error" in outcome) {
    return <ErrorNotice>{cfuErrorText(outcome.error, strings)}</ErrorNotice>;
  }
  const r = outcome.result;
  const summary = t(strings.copySummary, { value: sig(fmt, r.cfuPerMl, 3) });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.cfuPerMl}
          value={sig(fmt, r.cfuPerMl, 3)}
        />
        <StatCard
          label={strings.meanColonies}
          value={fixed(fmt, r.mean, Number.isInteger(r.mean) ? 0 : 1)}
        />
        <StatCard
          label={strings.platesCounted}
          value={plain(fmt, input.counts.length)}
        />
      </StatGrid>
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{strings.platesHeading}</h3>
        <ul className="list-disc space-y-1 ps-5 text-foreground text-sm">
          {input.counts.map((count, index) => (
            // Counts can repeat, so the position is the only identity.
            // biome-ignore lint/suspicious/noArrayIndexKey: plates by position
            <li key={index}>{plateLine(index, count, range, fmt, strings)}</li>
          ))}
        </ul>
      </div>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.workingCfu}</p>
        <p>
          {t(strings.workingCfuNumbers, {
            mean: plain(fmt, r.mean),
            dilution: sig(fmt, input.cumulative * input.preDilution, 6),
            plated: plain(fmt, input.platedMl),
            result: sig(fmt, r.cfuPerMl, 3),
          })}
        </p>
      </div>
      <Notice>{strings.cfuNote}</Notice>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
    </>
  );
}
